import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./Header.css";
import axios from "axios";
import API_BASE_URL from "../config";

export default function Header({ onToggleSidebar }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isOperator, setIsOperator] = useState(false);
  const [impersonatedCompany, setImpersonatedCompany] = useState(localStorage.getItem("impersonatedCompanyName"));
  const [pendingCheckout, setPendingCheckout] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const checkoutSafeRoutes = ["/plans", "/billing/success", "/billing/cancel"];

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const clearImpersonationFlags = () => {
    ["impersonatedCompanyName", "impersonatedCompanyId", "impersonatedUserId", "impersonatedUserEmail"].forEach((key) =>
      localStorage.removeItem(key)
    );
    sessionStorage.removeItem("opsOriginSession");
    setImpersonatedCompany(null);
  };

  const handleLogout = () => {
    clearImpersonationFlags();
    localStorage.clear();
    sessionStorage.clear();
    navigate("/login", { replace: true });
    window.location.reload();
  };

  const exitImpersonation = () => {
    const backup = sessionStorage.getItem("opsOriginSession");
    if (!backup) {
      handleLogout();
      return;
    }

    try {
      const parsed = JSON.parse(backup);
      if (parsed.token) {
        localStorage.setItem("userToken", parsed.token);
      } else {
        localStorage.removeItem("userToken");
      }
      if (parsed.role) {
        localStorage.setItem("userRole", parsed.role);
      } else {
        localStorage.removeItem("userRole");
      }
    } catch (err) {
      console.error("Failed to restore operator session", err);
      handleLogout();
      return;
    }

    clearImpersonationFlags();
    navigate("/ops/companies", { replace: true });
  };

  // Ensure the session is valid and the company has an active plan
  useEffect(() => {
    const ensurePlanAccess = async () => {
      const token = localStorage.getItem("userToken");
      if (!token) {
        handleLogout();
        return;
      }

      const role = (localStorage.getItem("userRole") || "").toLowerCase();
      setIsOperator(role === "support" || role === "superadmin");
      setImpersonatedCompany(localStorage.getItem("impersonatedCompanyName"));

      let payload;
      try {
        payload = JSON.parse(atob(token.split(".")[1]));
      } catch (err) {
        console.error("Invalid token payload:", err.message);
        handleLogout();
        return;
      }

      const now = Date.now() / 1000;
      if (payload?.exp && payload.exp < now) {
        console.warn("Token expired. Logging out...");
        handleLogout();
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/subscription`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const needsCheckout = Boolean(response.data?.requiresCheckout);
        setPendingCheckout(needsCheckout);
        if (response.data?.companyStatus) {
          localStorage.setItem("companyStatus", response.data.companyStatus);
        }

        const allowRoute = checkoutSafeRoutes.some((path) => location.pathname.startsWith(path));
        if (needsCheckout && !allowRoute) {
          navigate("/plans", { replace: true });
        }
      } catch (error) {
        console.error(
          "Error verifying company plan:",
          error.response?.data || error.message
        );

        if (error.response?.status === 401) {
          handleLogout();
        }
        setPendingCheckout(false);
      }
    };

    ensurePlanAccess();
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const className = "impersonation-active";
    document.body.classList.toggle(className, Boolean(impersonatedCompany));
    return () => {
      document.body.classList.remove(className);
    };
  }, [impersonatedCompany]);

  const toggleSidebar = () => {
    if (typeof onToggleSidebar === "function") {
      onToggleSidebar();
    } else if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("pairfon-sidebar-toggle"));
    }
  };

  return (
    <>
      {impersonatedCompany && (
        <div className="impersonation-banner text-center">
          <span>
            You are impersonating <strong>{impersonatedCompany}</strong>
          </span>
          <button className="btn btn-sm btn-light ms-3" onClick={exitImpersonation}>
            Exit impersonation
          </button>
        </div>
      )}
      {pendingCheckout && (
        <div
          className="checkout-banner"
          style={{ top: impersonatedCompany ? "40px" : "0" }}
        >
          <span>Complete plan checkout to activate your Pairfon trial.</span>
          <button className="checkout-banner-btn" onClick={() => navigate("/plans", { replace: true })}>
            Choose plan
          </button>
        </div>
      )}
      <nav
        className="navbar navbar-expand-lg fixed-top"
        style={{
          top: `${(impersonatedCompany ? 40 : 0) + (pendingCheckout ? 44 : 0)}px`,
        }}
      >
        <div className="container-fluid navbar-content">
          <button
            type="button"
            className="mobile-nav-trigger d-lg-none"
            aria-label="Open navigation"
            onClick={toggleSidebar}
          >
            <i className="bi bi-list" />
          </button>
          <div className="navbar-icons ms-auto d-flex align-items-center">
            {isOperator && (
              <button
                className="btn btn-outline-secondary me-3"
                onClick={() => navigate("/ops/overview")}
              >
                Ops Console
              </button>
            )}
            <div className="dropdown">
              <div
                className="header-avatar"
                onClick={toggleDropdown}
                title="User Menu"
              >
                <i className="bi bi-person-fill" />
              </div>
              {dropdownOpen && (
                <div className="dropdown-menu show">
                  <button className="dropdown-item" onClick={() => { setDropdownOpen(false); navigate("/profile"); }}>
                    <i className="bi bi-person"></i> Profile
                  </button>
                  <button className="dropdown-item" onClick={() => { setDropdownOpen(false); navigate("/billing"); }}>
                    <i className="bi bi-credit-card"></i> Billing
                  </button>
                  {impersonatedCompany && (
                    <button className="dropdown-item" onClick={exitImpersonation}>
                      <i className="bi bi-arrow-return-left"></i> Exit impersonation
                    </button>
                  )}
                  <div className="dropdown-divider" />
                  <button className="dropdown-item dropdown-item-danger" onClick={handleLogout}>
                    <i className="bi bi-box-arrow-right"></i> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}
